import React, { Suspense } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { PropTypes } from "prop-types";
import { generatePath, useNavigate } from "react-router-dom";
import { withI18n } from "../../../i18n/useI18n";
import _ from "lodash";
import reactWrapper from "@harbor/elements/utils/react/wrapper";

import css from "../applicationsMagneticStyle.less";
import * as actions from "../actions";
import Spinner from "../../../common/Spinner";
import AppHOC from "../../../generics/AppHOC";
import {
  formatString,
  formatBytes,
  formatNumberNDecimalPlaces,
  formatNumber
} from "../../../utils/format";
import { hasFilterChanged } from "../../../utils/common";
import { routeURLs, TOGGLER } from "../../../utils/enums";

const NoDataAvailable = React.lazy(() =>
  import("../../../common/NoDataAvailable")
);
const ErrorComponent = React.lazy(() =>
  import("../../../common/ErrorComponent")
);
const TableTrendingApplications = React.lazy(() =>
  import("../../../common/TableTrendingApplications")
);

const [HbrCard, HbrSelect, HbrOption, HbrTable] = reactWrapper([
  "hbr-card",
  "hbr-select",
  "hbr-option",
  "hbr-table"
]);

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch)
});

const mapStateToProps = state => ({
  applications: state.vanalytics.applications
});

// APP_RISE_FILTERS will be created inside the component to use translations

class TrendingAppsView extends React.Component {
  constructor() {
    super();
    this.state = {
      appQoEData: null,
      appUsageData: null,
      appRiseDropdownValueData: "usage",
      appdataProcessed: false
    };
  }

  getAppRiseFilters = () => {
    const { tViews } = this.props.i18n;
    return [
      { label: tViews('trendingAppsViewBandwidth'), value: "usage" },
      { label: tViews('trendingAppsViewLoss'), value: "loss" },
      { label: tViews('trendingAppsViewLatency'), value: "latency" }
    ];
  };

  getData = () => {
    this.setState({
      appQoEData: getFilteredData(this.props.globalFilter, "vqoe"),
      appUsageData: getFilteredData(this.props.globalFilter, "usage"),
      appRiseDropdownValueData: "usage",
      appdataProcessed: true
    });
    // callback to application summary report that data is present for trend apps widget
    this.props.isReportTrendCallback?.(true);
    this.props.actions.setTrendingAppsFilter("RISE", "usage");
  };

  componentDidMount() {
    if (
      this?.props?.globalFilter?.app_data_with_family_sla != undefined &&
      this?.props?.globalFilter?.app_data_with_family_sla &&
      this?.props?.globalFilter?.app_data_with_family_sla.length > 0 &&
      !this.state.appdataProcessed
    ) {
      this.getData();
    }
  }

  componentDidUpdate(prevProps) {
    if (hasFilterChanged(this.props.globalFilter, prevProps.globalFilter)) {
      this.setState({
        appdataProcessed: false
      });
      this.props.actions.setTrendingAppsFilter("RISE", "usage");
    }
    if (
      !_.isEqual(
        this.props.globalFilter.app_data_with_family_sla,
        prevProps.globalFilter.app_data_with_family_sla
      )
    ) {
      if (
        this?.props?.globalFilter?.app_data_with_family_sla != undefined &&
        this?.props?.globalFilter?.app_data_with_family_sla &&
        this?.props?.globalFilter?.app_data_with_family_sla.length > 0 &&
        !this.state.appdataProcessed
      ) {
        this.getData();
      } else if (
        this?.props?.globalFilter?.app_data_with_family_sla != undefined &&
        this?.props?.globalFilter?.app_data_with_family_sla &&
        this?.props?.globalFilter?.app_data_with_family_sla.length == 0 &&
        !this.state.appdataProcessed
      ) {
        // callback to application summary report that data is not present for trend apps widget
        this.props.isReportTrendCallback?.(true);
        this.setState({
          appQoEData: [],
          appUsageData: [],
          appRiseDropdownValueData: "usage",
          appdataProcessed: true
        });
      }
    }
  }

  onChangeTrend(evt) {
    const type = "RISE";
    const filter = evt;
    const { setTrendingAppsFilter } = this.props.actions;
    setTrendingAppsFilter(type, filter);
    // reload dashlet data
    if (type === "RISE") {
      this.setState({
        appUsageData: getFilteredData(this.props.globalFilter, filter),
        appRiseDropdownValueData: filter
      });
    }
  }

  getTableData = () => {
    const { trendingAppsFilter } = this.props.applications;
    const appData = this.state.appUsageData.data.map(item => {
      if (trendingAppsFilter.rise === "usage") {
        item.previousTableData = formatBytes(item.previous);
        item.currentTableData = formatBytes(item.current);
        if (Math.sign(item.change) < 0) {
          item.changeTableData = `-${formatBytes(Math.abs(item.change))}`;
        } else {
          item.changeTableData = formatBytes(Math.abs(item.change));
        }
      } else {
        item.previousTableData = formatNumberNDecimalPlaces(item.previous);
        item.currentTableData = formatNumberNDecimalPlaces(item.current);
        if (trendingAppsFilter.rise === "loss")
          item.changeTableData = formatNumber(item.change, "changePercent", 1);
        else if (trendingAppsFilter.rise === "latency")
          item.changeTableData =
            formatNumber(item.change, "default", 1) + " ms";
      }
      item.applicationTableData = formatString(item.application, 16);
      return item;
    });
    return appData;
  };

  render() {
    const { tViews } = this.props.i18n;
    const APP_RISE_FILTERS = this.getAppRiseFilters();

    const getApplicationDetails = application => {
      if(this?.props?.globalFilter?.selectedSite === -1)
        this.props.setPageToggle(TOGGLER.MAP)
      this.props.navigate(generatePath(routeURLs.app360, { appName: application }));
    };

    const columns = [
      {
        prop: "applicationTableData",
        name: tViews('appViewApplication'),
        size: 130,
        readonly: true,
        cellProperties: () => {
          return {
            class: {
              "hbr-table-cell-readonly-background-color": true
            }
          };
        },
        cellTemplate: (createElement, props) => {
          const content = createElement("div", {
            innerHTML: `
                  <hbr-link class="application-trending-apps-qoe-widget-link" id="app-trend-rise-app-qoe-${props.model.application}">${props.model.applicationTableData}</hbr-link>
                  <hbr-portal>
                    <hbr-tooltip anchor="#app-trend-rise-app-qoe-${props.model.application}">
                      <div slot="content">
                          <strong>${props.model.application}</strong>  
                      </div>
                    </hbr-tooltip>
                  </hbr-portal>
                `,
            onClick: e => {
              if (e && e.target) {
                getApplicationDetails(props.model.application);
              }
            }
          });
          return content;
        }
      },
      {
        prop: "previousTableData",
        name: tViews('trendingAppsPrevious'),
        size: 93,
        readonly: true,
        cellProperties: () => {
          return {
            class: {
              "hbr-table-cell-readonly-background-color": true
            }
          };
        }
      },
      {
        prop: "currentTableData",
        name: tViews('trendingAppsCurrent'),
        size: 70,
        readonly: true,
        cellProperties: () => {
          return {
            class: {
              "hbr-table-cell-readonly-background-color": true
            }
          };
        }
      },
      {
        prop: "changeTableData",
        name: tViews('trendingAppsChange'),
        size: 103,
        readonly: true,
        cellProperties: () => {
          return {
            class: {
              "hbr-table-cell-readonly-background-color": true
            }
          };
        }
      }
    ];

    let finaldata;
    if (
      this.state.appUsageData &&
      this.state.appUsageData.data &&
      this.state.appUsageData.data.length > 0
    ) {
      finaldata = this.getTableData();
    }
    const source = finaldata;

    const { timeFilter } = this.props.globalFilter;
    const loader =
      this?.props?.globalFilter?.app_data_with_family_sla == undefined;
    const errorTrendingApplications =
      this?.props?.globalFilter?.applicationAggregateData &&
      this?.props?.globalFilter?.applicationAggregateData.resObj &&
      this?.props?.globalFilter?.applicationAggregateData.resObj.message !=
      "successful";
    return (
      <>
      {this.props.reporting && (
        <div className="qoe-distribution-view-title">
        <div className="hbr-type-h2">{tViews('trendingApplications')}</div>
        </div> 
      )} 
      <div className={css["trending-apps"]} data-cy="trendingAppsView">
        <div className="trend-application-drop-qoe">
          <HbrCard id="non-interactive-most-drop" className="trendingAppCard">
            <div
              className={
                this.props.reporting
                  ? "trending-applications-tables--drop"
                  : "trending-applications-ui-drop"
              }
              data-cy="droppingApps"
            >
              <div className="hbr-type-h2 trendingTitle" role="heading" aria-level={'2'}>{tViews('mostAvgDropOf')}</div>

              {loader === true ? (
                <div className="home-spinner-25" data-cy="spinner-componentAppsByDropView">
                  <Spinner />
                </div>
              ) : (
                <div>
                  {this.state.appQoEData &&
                    this.state.appQoEData.data &&
                    this.state.appQoEData.data.length &&
                    loader === false ? (
                    <Suspense fallback={<Spinner />}>
                      <TableTrendingApplications
                        {...this.props}
                        applications={this.state.appQoEData.data}
                        timeFilter={timeFilter}
                      />
                    </Suspense>
                  ) : errorTrendingApplications ? (
                    <Suspense fallback={<Spinner />}>
                      <ErrorComponent
                        width={"110px"}
                        className={"super-small-dashlet-error"}
                        errorCode={
                          this.props.globalFilter.applicationAggregateData
                            .resObj.errorCode
                        }
                        message={
                          this.props.globalFilter.applicationAggregateData
                            .resObj.message
                        }
                      />
                    </Suspense>
                  ) : (
                    <Suspense fallback={<Spinner />}>
                      <NoDataAvailable
                        text={tViews('applicationDashboardNoData')}
                      />
                    </Suspense>
                  )}
                </div>
              )}
            </div>
          </HbrCard>
        </div>

        {/*  most avg rise of trending widget section   */}
        <div className="trend-application-rise-qoe">
          <HbrCard id="non-interactive" slot="label" className="trendTableCard">
            <div
              className={
                this.props.reporting
                  ? "trending-applications-tables--rise"
                  : "trending-applications-ui-rise"
              }
              data-cy="risingApps"
            >
              <div className="linespacing-container">
                <div className="hbr-type-h2 trendingTitle" role="heading" aria-level={'2'}>
                  {tViews('mostAvgRiseOf')}
                </div>
                <HbrSelect
                  value={this.state.appRiseDropdownValueData}
                  onHbr-change={event => {
                    this.onChangeTrend(event.currentTarget.value);
                  }}
                  name="trendDropDown"
                  size="small"
                  className="trendDropDown"
                >
                  {APP_RISE_FILTERS.map(item => (
                    <HbrOption key={item.value} value={item.value}>
                      {item.label}
                    </HbrOption>
                  ))}
                </HbrSelect>
              </div>
              {loader === true ? (
                <div className="home-spinner-25" data-cy="spinner-componentAppsByRiseView">
                  <Spinner />
                </div>
              ) : (
                <div className="usage-trend">
                  {this.state.appUsageData &&
                    this.state.appUsageData.data &&
                    this.state.appUsageData.data.length &&
                    loader === false ? (
                    <HbrTable
                      className="trendTableRise"
                      readonly
                      canFocus={false}
                      columns={columns}
                      source={source}
                    />
                  ) : errorTrendingApplications ? (
                    <Suspense fallback={<Spinner />}>
                      <ErrorComponent
                        width={"110px"}
                        className={"super-small-dashlet-error"}
                        errorCode={
                          this.props.globalFilter.applicationAggregateData
                            .resObj.errorCode
                        }
                        message={
                          this.props.globalFilter.applicationAggregateData
                            .resObj.message
                        }
                      />
                    </Suspense>
                  ) : (
                    <Suspense fallback={<Spinner />}>
                      <NoDataAvailable
                        text={tViews('applicationDashboardNoData')}
                      />
                    </Suspense>
                  )}
                </div>
              )}
            </div>
          </HbrCard>
        </div>
      </div>
      </>
    );
  }
}

TrendingAppsView.propTypes = {
  actions: PropTypes.object.isRequired,
  globalFilter: PropTypes.object.isRequired,
  applications: PropTypes.object.isRequired,
  reporting: PropTypes.bool,
  navigate: PropTypes.func.isRequired,
  isReportTrendCallback: PropTypes.func,
  setPageToggle: PropTypes.func,
  i18n: PropTypes.object.isRequired
};
TrendingAppsView.defaultProps = {
  reporting: false
};

const resPropNames = {
  usage: ["usage", "prev_usage", "usage_change", "usage_change_output"],
  loss:
    ["packet_loss", "prev_packet_loss", "loss_change", "loss_change_output"],
  latency:
    ["latency", "prev_latency", "latency_change", "latency_change_output"],
  vqoe:
    ["vqoe_score", "prev_vqoe_score", "vqoe_change", "vqoe_change_output"]
};

const getFilteredData = (gFilter, filter) => {
  const resData = gFilter.app_data_with_family_sla;
  const result = { data: [] };
  if (Array.isArray(resData) && resData.length > 0) {
    const propNames = resPropNames[filter];
    const sortField = propNames[3];
    const data = resData.filter(item => item.vqoe_status !== "unknown");
    if (filter === "vqoe") data
      .sort((item1, item2) => item1[sortField] < item2[sortField] ? -1 : 1);
    else data
      .sort((item1, item2) => item1[sortField] > item2[sortField] ? -1 : 1);
    result.data = data.slice(0, 5).map(item => ({
      application: item.application,
      current: item[propNames[0]],
      previous: item[propNames[1]],
      change: item[propNames[2]],
      change_percent: item[propNames[3]]
    }));
    if (filter === "loss") result.data.sort((a, b) => b.change - a.change);
  }
  return result;
};

const TrendingAppsViewWithNavigate = props => {
  const navigate = useNavigate();
  return <TrendingAppsView {...props} navigate={navigate} />;
};

export default (
  connect(mapStateToProps, mapDispatchToProps)(AppHOC(withI18n(TrendingAppsViewWithNavigate)))
);
